import { Command as CommandPrimitive } from 'cmdk'
import { CheckIcon, ChevronsUpDownIcon, SearchIcon } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface ComboboxOption {
  value: string
  label: string
  hint?: string
  prefix?: ReactNode
  keywords?: string[]
}

/** Searchable select built on Radix Popover + cmdk. Keyboard friendly; virtualisation not needed for <1k options. */
export function Combobox({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No results',
  id,
  disabled,
  allowClear = false,
  className,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: {
  value: string | null | undefined
  onChange: (value: string | null) => void
  options: ComboboxOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  id?: string
  disabled?: boolean
  allowClear?: boolean
  className?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}) {
  const [open, setOpen] = useState(false)
  const selected = useMemo(() => options.find((o) => o.value === value), [options, value])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          disabled={disabled}
          className={cn(
            'flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-input bg-card px-3 text-left text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-primary/60 focus-visible:ring-[3px] focus-visible:ring-ring/60 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive dark:bg-input/30',
            className,
          )}
        >
          <span className={cn('flex min-w-0 items-center gap-2 truncate', !selected && 'text-muted-foreground')}>
            {selected?.prefix}
            <span className="truncate">{selected?.label ?? value ?? placeholder}</span>
            {selected?.hint && <span className="shrink-0 text-xs text-muted-foreground">{selected.hint}</span>}
          </span>
          <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) min-w-64 p-0" align="start">
        <CommandPrimitive className="flex flex-col">
          <div className="flex h-10 items-center gap-2 border-b px-3">
            <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
            <CommandPrimitive.Input placeholder={searchPlaceholder} className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground" />
          </div>
          <CommandPrimitive.List className="max-h-64 overflow-y-auto p-1 scrollbar-thin">
            <CommandPrimitive.Empty className="py-6 text-center text-sm text-muted-foreground">{emptyText}</CommandPrimitive.Empty>
            {allowClear && value && (
              <CommandPrimitive.Item
                value="__clear__"
                onSelect={() => {
                  onChange(null)
                  setOpen(false)
                }}
                className="flex cursor-default items-center rounded-md px-2 py-1.5 text-sm text-muted-foreground outline-none data-[selected=true]:bg-muted"
              >
                Clear selection
              </CommandPrimitive.Item>
            )}
            {options.map((option) => (
              <CommandPrimitive.Item
                key={option.value}
                value={option.value}
                keywords={[option.label, ...(option.keywords ?? [])]}
                onSelect={() => {
                  onChange(option.value)
                  setOpen(false)
                }}
                className="flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
              >
                {option.prefix}
                <span className="truncate">{option.label}</span>
                {option.hint && <span className="ml-auto shrink-0 pl-2 text-xs text-muted-foreground">{option.hint}</span>}
                <CheckIcon className={cn('size-4 shrink-0 text-primary', option.hint ? 'ml-1' : 'ml-auto', option.value === value ? 'opacity-100' : 'opacity-0')} />
              </CommandPrimitive.Item>
            ))}
          </CommandPrimitive.List>
        </CommandPrimitive>
      </PopoverContent>
    </Popover>
  )
}
